import { BoxOfferInvitationEntity } from "@modules/boxOffer/entities/boxOfferInvitationEntity"
import {
  BoxOfferAutonomousPurchase,
  BoxOfferPriceType,
  BoxOfferRule,
  BoxOfferType,
  BoxOfferValidity,
  BoxOfferWeeklyParticipationType,
  Price,
} from "@modules/boxOffer/entities/boxOfferTypes"
import {
  BoxOfferDetailedDto,
  BoxOfferDraftDto,
  BoxOfferItemDto,
  BoxOfferMobileDetailedDto,
  MobileBoxProgramRefDto,
} from "@modules/boxOffer/gateway/boxOfferDto"
import { PriceObjectDto } from "@modules/common/priceObject/gateway/priceObjectApiDto"
import { word } from "@modules/core/utils/i18n"
import { convertImageFromDto } from "@modules/dtos/imageDto"
import { ProgramType } from "@modules/programs/programTypes"
import { HUImage } from "@modules/types/imageTypes"
import { convertToEnum } from "@modules/utils/enum"
import dayjs from "dayjs"

export class BoxOfferItemEntity {
  id: string
  type: BoxOfferType
  name: string
  description?: string
  price: Price | null
  minimumCommitmentPeriod: string | null
  count?: number | null
  currency: string | null
  isFree: boolean
  archived: boolean

  private constructor(boxOfferItem: BoxOfferItemEntity) {
    this.id = boxOfferItem.id
    this.type = boxOfferItem.type
    this.name = boxOfferItem.name
    this.description = boxOfferItem.description
    this.price = boxOfferItem.price
    this.minimumCommitmentPeriod = boxOfferItem.minimumCommitmentPeriod
    this.count = boxOfferItem.count
    this.currency = boxOfferItem.currency
    this.isFree = boxOfferItem.isFree
    this.archived = boxOfferItem.archived
  }

  static fromItemDto(dto: BoxOfferItemDto): BoxOfferItemEntity {
    return new BoxOfferItemEntity({
      ...dto,
      price: dto.priceObject ?? null,
      minimumCommitmentPeriod: dto.minimumCommitmentPeriod ?? null,
    })
  }
}

export class BoxOfferDetailedEntity {
  id: string
  type: BoxOfferType
  name: string
  description?: string
  price: Price | null
  minimumCommitmentPeriod: string | null
  count?: number
  maxWeeklyParticipation?: number
  validityPeriodMonths?: number
  renewable?: boolean
  showcaseDisplay: boolean
  priceDisplay: boolean
  autonomousPurchase!: BoxOfferAutonomousPurchase
  archived: boolean
  programs: string[]
  rules: BoxOfferRule[]
  minimumMonthlyDuration?: number
  /**
   * Deprecated
   */
  currency?: string
  isFree: boolean

  private constructor(boxOffer: BoxOfferDetailedEntity) {
    this.id = boxOffer.id
    this.type = boxOffer.type
    this.name = boxOffer.name
    this.description = boxOffer.description
    this.price = boxOffer.price
    this.minimumCommitmentPeriod = boxOffer.minimumCommitmentPeriod
    this.count = boxOffer.count
    this.maxWeeklyParticipation = boxOffer.maxWeeklyParticipation
    this.validityPeriodMonths = boxOffer.validityPeriodMonths
    this.renewable = boxOffer.renewable
    this.showcaseDisplay = boxOffer.showcaseDisplay
    this.priceDisplay = boxOffer.priceDisplay
    this.autonomousPurchase = boxOffer.autonomousPurchase
    this.archived = boxOffer.archived
    this.programs = boxOffer.programs
    this.rules = boxOffer.rules
    this.currency = boxOffer.currency
    this.isFree = boxOffer.isFree
    this.minimumMonthlyDuration = boxOffer.minimumMonthlyDuration
  }

  static fromDto(dto: BoxOfferDetailedDto): BoxOfferDetailedEntity {
    const offer = new BoxOfferDetailedEntity({
      ...dto,
      price: dto.priceObject ?? null,
      description: dto.description ?? undefined,
      count: dto.count ?? undefined,
      maxWeeklyParticipation: dto.maxWeeklyParticipation ?? undefined,
      validityPeriodMonths: dto.validityPeriodMonths ?? undefined,
      renewable: dto.renewable ?? undefined,
      currency: dto.currency ?? undefined,
    })

    switch (dto.type) {
      case BoxOfferType.Subscription:
        offer.minimumMonthlyDuration = dayjs.duration(dto.minimumCommitmentPeriod!).months()
        break
      case BoxOfferType.Trial:
        offer.count = offer.count ?? 0
        offer.renewable = !!offer.renewable
        break
      case BoxOfferType.TicketsBook:
        offer.count = offer.count ?? 0
        break
      case BoxOfferType.Millenium:
        offer.isFree = true
        offer.price = null
        break
    }

    return offer
  }
}

export class BoxOfferDraftEntity {
  type: BoxOfferType
  name: string
  description: string
  priceSwitch: BoxOfferPriceType
  price: number | null
  currency?: string
  weeklyParticipationSwitch: BoxOfferWeeklyParticipationType
  maxWeeklyParticipation: number | undefined
  minimumMonthlyDuration?: number
  programs: string[]
  rules?: BoxOfferRule[]
  renewable?: boolean
  validityPeriodMonths?: number
  validitySwitch: BoxOfferValidity
  showcaseDisplay: boolean
  priceDisplay: boolean
  autonomousPurchase: BoxOfferAutonomousPurchase
  archived?: boolean
  /**
   * Deprecated
   */
  count?: number

  private constructor(boxOfferDraft: BoxOfferDraftEntity) {
    this.type = boxOfferDraft.type
    this.name = boxOfferDraft.name
    this.description = boxOfferDraft.description
    this.priceSwitch = boxOfferDraft.priceSwitch
    this.price = boxOfferDraft.price
    this.currency = boxOfferDraft.currency
    this.weeklyParticipationSwitch = boxOfferDraft.weeklyParticipationSwitch
    this.maxWeeklyParticipation = boxOfferDraft.maxWeeklyParticipation
    this.minimumMonthlyDuration = boxOfferDraft.minimumMonthlyDuration
    this.programs = boxOfferDraft.programs
    this.rules = boxOfferDraft.rules
    this.renewable = boxOfferDraft.renewable
    this.validityPeriodMonths = boxOfferDraft.validityPeriodMonths
    this.validitySwitch = boxOfferDraft.validitySwitch
    this.showcaseDisplay = boxOfferDraft.showcaseDisplay
    this.priceDisplay = boxOfferDraft.priceDisplay
    this.autonomousPurchase = boxOfferDraft.autonomousPurchase
    this.archived = boxOfferDraft.archived
    this.count = boxOfferDraft.count
  }

  static toDraft(
    boxOfferDraft?: BoxOfferDetailedEntity,
    selectedBoxOfferType?: BoxOfferType,
    defaultCurrency?: string,
  ): BoxOfferDraftEntity {
    return {
      type: boxOfferDraft?.type ?? selectedBoxOfferType ?? BoxOfferType.Subscription,
      name: boxOfferDraft?.name ?? "",
      description: boxOfferDraft?.description ?? "",
      priceSwitch:
        boxOfferDraft?.isFree ||
        (selectedBoxOfferType && [BoxOfferType.Trial, BoxOfferType.Millenium].includes(selectedBoxOfferType))
          ? BoxOfferPriceType.Free
          : BoxOfferPriceType.Paid,
      price: boxOfferDraft?.price?.value ?? null,
      currency: boxOfferDraft?.price?.currency ?? defaultCurrency ?? "EUR",
      programs: boxOfferDraft?.programs ?? [],
      count:
        boxOfferDraft?.type === BoxOfferType.TicketsBook || boxOfferDraft?.type === BoxOfferType.Trial
          ? boxOfferDraft.count
          : undefined,
      minimumMonthlyDuration:
        boxOfferDraft?.type === BoxOfferType.Subscription
          ? boxOfferDraft.minimumMonthlyDuration
          : selectedBoxOfferType === BoxOfferType.Subscription
            ? 1
            : undefined,
      weeklyParticipationSwitch: boxOfferDraft?.maxWeeklyParticipation
        ? BoxOfferWeeklyParticipationType.Restricted
        : BoxOfferWeeklyParticipationType.Unlimited,
      maxWeeklyParticipation: boxOfferDraft?.maxWeeklyParticipation,
      rules: boxOfferDraft?.rules ?? [],
      renewable:
        boxOfferDraft?.type === BoxOfferType.Trial
          ? boxOfferDraft.renewable
          : selectedBoxOfferType === BoxOfferType.Trial
            ? false
            : undefined,
      validityPeriodMonths: boxOfferDraft?.validityPeriodMonths,
      validitySwitch: boxOfferDraft?.validityPeriodMonths ? BoxOfferValidity.DEFINED : BoxOfferValidity.UNLIMITED,
      showcaseDisplay: boxOfferDraft?.showcaseDisplay ?? true,
      priceDisplay: boxOfferDraft?.priceDisplay ?? true,
      autonomousPurchase: convertToEnum(
        BoxOfferAutonomousPurchase,
        boxOfferDraft?.autonomousPurchase,
        BoxOfferAutonomousPurchase.PUBLIC,
      ),
      archived: boxOfferDraft?.archived ?? false,
    }
  }

  static toDto(draft: BoxOfferDraftEntity): BoxOfferDraftDto {
    return {
      type: draft.type,
      name: draft.name,
      description: draft.description,
      priceObject:
        draft.priceSwitch === BoxOfferPriceType.Paid
          ? ({ value: draft.price ?? 0, currency: draft.currency } as PriceObjectDto)
          : undefined,
      programs: draft.programs,
      count:
        (draft.type === BoxOfferType.TicketsBook || draft.type === BoxOfferType.Trial) && draft.count
          ? draft.count
          : undefined,
      minimumCommitmentPeriod:
        draft.type === BoxOfferType.Subscription
          ? dayjs.duration({ months: draft.minimumMonthlyDuration }).toISOString()
          : undefined,
      maxWeeklyParticipation:
        draft.weeklyParticipationSwitch === BoxOfferWeeklyParticipationType.Restricted
          ? draft.maxWeeklyParticipation!
          : null,
      rules: draft.rules,
      renewable: draft.renewable,
      validityPeriodMonths: draft.validityPeriodMonths,
      showcaseDisplay: draft.showcaseDisplay ?? true,
      priceDisplay: draft.priceDisplay ?? true,
      autonomousPurchase: draft.autonomousPurchase ?? BoxOfferAutonomousPurchase.PUBLIC,
      archived: draft.archived ?? false,
    }
  }
}

export class MobileBoxProgramRef {
  id: string
  name: string
  image: HUImage | null
  type: ProgramType

  private constructor(program: MobileBoxProgramRef) {
    this.id = program.id
    this.name = program.name
    this.image = program.image
    this.type = program.type
  }

  static fromDto(dto: MobileBoxProgramRefDto): MobileBoxProgramRef {
    return new MobileBoxProgramRef({
      ...dto,
      image: dto.image ? convertImageFromDto(dto.image) : null,
    })
  }
}

export type BoxOfferMobileDetailedState = Omit<BoxOfferMobileDetailedEntity, "getBoxOfferTypeWord">
export class BoxOfferMobileDetailedEntity {
  id: string
  type: BoxOfferType
  name: string
  description: string
  priceObject: Price | null
  minimumCommitmentPeriod: string | null
  count: number | null
  maxWeeklyParticipation: number | null
  validityPeriodMonths: number | null
  renewable: boolean | null
  showcaseDisplay: boolean
  priceDisplay: boolean
  autonomousPurchase: BoxOfferAutonomousPurchase
  canUserPurchase: boolean
  programs: MobileBoxProgramRef[]
  rules: BoxOfferRule[]
  currency: string | null
  hasAlreadyPurchase: boolean | null
  invitation: BoxOfferInvitationEntity | null

  private constructor(boxOffer: BoxOfferMobileDetailedState) {
    this.id = boxOffer.id
    this.type = boxOffer.type
    this.name = boxOffer.name
    this.description = boxOffer.description
    this.priceObject = boxOffer.priceObject
    this.minimumCommitmentPeriod = boxOffer.minimumCommitmentPeriod
    this.count = boxOffer.count
    this.maxWeeklyParticipation = boxOffer.maxWeeklyParticipation
    this.validityPeriodMonths = boxOffer.validityPeriodMonths
    this.renewable = boxOffer.renewable
    this.showcaseDisplay = boxOffer.showcaseDisplay
    this.priceDisplay = boxOffer.priceDisplay
    this.autonomousPurchase = boxOffer.autonomousPurchase
    this.canUserPurchase = boxOffer.canUserPurchase
    this.programs = boxOffer.programs
    this.rules = boxOffer.rules
    this.currency = boxOffer.currency
    this.hasAlreadyPurchase = boxOffer.hasAlreadyPurchase
    this.invitation = boxOffer.invitation
  }

  static fromDto(dto: BoxOfferMobileDetailedDto): BoxOfferMobileDetailedEntity {
    return new BoxOfferMobileDetailedEntity({
      ...dto,
      minimumCommitmentPeriod: dto.minimumCommitmentPeriod
        ? dayjs.duration(dto.minimumCommitmentPeriod).months().toString()
        : null,
      priceObject: dto.priceObject ?? null,
      programs: dto.programs.map((program) => MobileBoxProgramRef.fromDto(program)),
      description: dto.description ?? "",
      invitation: dto.invitation ? BoxOfferInvitationEntity.fromDto(dto.invitation) : null,
    })
  }

  getBoxOfferTypeWord = () => {
    switch (this.type) {
      case BoxOfferType.Subscription:
        return word("box.offer.type.per.month")
      case BoxOfferType.DropIn:
      case BoxOfferType.Trial:
      case BoxOfferType.TicketsBook:
        return word("box.offer.type.per.session", { count: this.count ?? 1 })
      case BoxOfferType.Millenium:
        return ""
    }
  }
}

export type BoxOfferMobileItemState = Omit<BoxOfferMobileItemEntity, "getBoxOfferTypeWord">

export class BoxOfferMobileItemEntity {
  id: string
  type: BoxOfferType
  name: string
  description: string
  priceObject: Price | null
  minimumCommitmentPeriod: string | null
  count?: number | null
  validityPeriodMonths: number | null
  renewable: boolean | null
  showcaseDisplay: boolean
  priceDisplay: boolean
  autonomousPurchase: BoxOfferAutonomousPurchase
  canUserPurchase: boolean
  rules: BoxOfferRule[]
  currency: string | null

  private constructor(boxOffer: BoxOfferMobileItemState) {
    this.id = boxOffer.id
    this.type = boxOffer.type
    this.name = boxOffer.name
    this.description = boxOffer.description
    this.priceObject = boxOffer.priceObject
    this.minimumCommitmentPeriod = boxOffer.minimumCommitmentPeriod
    this.count = boxOffer.count
    this.validityPeriodMonths = boxOffer.validityPeriodMonths
    this.renewable = boxOffer.renewable
    this.showcaseDisplay = boxOffer.showcaseDisplay
    this.priceDisplay = boxOffer.priceDisplay
    this.autonomousPurchase = boxOffer.autonomousPurchase
    this.canUserPurchase = boxOffer.canUserPurchase
    this.rules = boxOffer.rules
    this.currency = boxOffer.currency
  }

  static fromDto(dto: BoxOfferMobileItemState): BoxOfferMobileItemEntity {
    return new BoxOfferMobileItemEntity({
      ...dto,
      priceObject: dto.priceObject ?? null,
      minimumCommitmentPeriod: dto.minimumCommitmentPeriod
        ? dayjs.duration(dto.minimumCommitmentPeriod).months().toString()
        : null,
      description: dto.description ?? "",
    })
  }
}
